// ============================================================================
// SHARED UTILITIES - Functional Programming Approach
// Consolidates common patterns found across multiple JS files
// ============================================================================

// ============================================================================
// DOM UTILITIES
// ============================================================================

/**
 * Pure function to get DOM element by selector
 * @param {string} selector - CSS selector
 * @returns {Element|null} DOM element or null if not found
 */
export const getElement = (selector) => document.querySelector(selector);

/**
 * Pure function to get all elements matching selector
 * @param {string} selector - CSS selector
 * @returns {NodeList} NodeList of matching elements
 */
export const getAllElements = (selector) => document.querySelectorAll(selector);

/**
 * Pure function to check if element exists (not null/undefined)
 * @param {*} element - Element to check
 * @returns {boolean} True if element exists
 */
export const elementExists = (element) => element !== null && element !== undefined;

/**
 * Pure function to get element's classList safely
 * @param {Element} element - DOM element
 * @returns {DOMTokenList|null} ClassList or null if element doesn't exist
 */
export const getClassList = (element) => elementExists(element) ? element.classList : null;

/**
 * Pure function to check if element has specific class
 * @param {Element} element - DOM element
 * @param {string} className - Class name to check
 * @returns {boolean} True if element has the class
 */
export const hasClass = (element, className) => {
    const classList = getClassList(element);
    return classList ? classList.contains(className) : false;
};

/**
 * Pure function to add class to element
 * @param {Element} element - DOM element
 * @param {string} className - Class name to add
 * @returns {boolean} True if operation successful
 */
export const addClass = (element, className) => {
    if (elementExists(element) && getClassList(element)) {
        getClassList(element).add(className);
        return true;
    }
    return false;
};

/**
 * Pure function to remove class from element
 * @param {Element} element - DOM element
 * @param {string} className - Class name to remove
 * @returns {boolean} True if operation successful
 */
export const removeClass = (element, className) => {
    if (elementExists(element) && getClassList(element)) {
        getClassList(element).remove(className);
        return true;
    }
    return false;
};

/**
 * Pure function to toggle class on element
 * @param {Element} element - DOM element
 * @param {string} className - Class name to toggle
 * @returns {boolean} True if operation successful
 */
export const toggleClass = (element, className) => {
    if (!elementExists(element)) return false;

    const classList = getClassList(element);
    if (!classList) return false;

    if (hasClass(element, className)) {
        return removeClass(element, className);
    } else {
        return addClass(element, className);
    }
};

// ============================================================================
// EVENT UTILITIES
// ============================================================================

/**
 * Pure function to attach event listener to element
 * @param {Element} element - DOM element
 * @param {string} eventType - Event type (e.g., 'click', 'scroll')
 * @param {Function} handler - Event handler function
 * @param {Object} options - Event listener options
 * @returns {boolean} True if operation successful
 */
export const attachEventListener = (element, eventType, handler, options = {}) => {
    if (elementExists(element)) {
        element.addEventListener(eventType, handler, options);
        return true;
    }
    return false;
};

/**
 * Pure function to attach delegated event listener
 * @param {Element} parent - Parent element to attach listener to
 * @param {string} eventType - Event type
 * @param {string} selector - CSS selector for target elements
 * @param {Function} handler - Event handler function
 * @param {Object} options - Event listener options
 * @returns {boolean} True if operation successful
 */
export const attachDelegatedEventListener = (parent, eventType, selector, handler, options = {}) => {
    if (!elementExists(parent)) return false;

    const delegatedHandler = (event) => {
        const target = event.target.closest(selector);
        if (target) {
            return handler(event, target);
        }
    };

    return attachEventListener(parent, eventType, delegatedHandler, options);
};

/**
 * Pure function to prevent default event behavior
 * @param {Event} event - Event object
 * @returns {boolean} True if operation successful
 */
export const preventDefault = (event) => {
    if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
        return true;
    }
    return false;
};

/**
 * Pure function to stop event propagation
 * @param {Event} event - Event object
 * @returns {boolean} True if operation successful
 */
export const stopPropagation = (event) => {
    if (event && typeof event.stopPropagation === 'function') {
        event.stopPropagation();
        return true;
    }
    return false;
};

// ============================================================================
// WINDOW & VIEWPORT UTILITIES
// ============================================================================

/**
 * Pure function to get current scroll position
 * @returns {number} Current scroll Y position
 */
export const getScrollY = () => window.scrollY;

/**
 * Pure function to get window width
 * @returns {number} Current window width
 */
export const getWindowWidth = () => window.innerWidth;

/**
 * Pure function to get window height
 * @returns {number} Current window height
 */
export const getWindowHeight = () => window.innerHeight;

/**
 * Pure function to check if viewport is at least specified width
 * @param {number} width - Minimum width to check
 * @param {number} breakpoint - Breakpoint to compare against (default: 768)
 * @returns {boolean} True if width is at or above breakpoint
 */
export const isDesktopBreakpoint = (width, breakpoint = 768) => width >= breakpoint;

/**
 * Pure function to check if element is in viewport
 * @param {Element} element - DOM element to check
 * @param {number} threshold - Visibility threshold (0-1)
 * @returns {boolean} True if element is visible
 */
export const isElementInViewport = (element, threshold = 0.1) => {
    if (!elementExists(element)) return false;

    const rect = element.getBoundingClientRect();
    const windowHeight = getWindowHeight();

    return (
        rect.top >= -rect.height * threshold &&
        rect.bottom <= windowHeight + rect.height * threshold
    );
};

// ============================================================================
// FUNCTIONAL UTILITIES
// ============================================================================

/**
 * Higher-order function to create debounced function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const createDebouncedFunction = (func, wait) => {
    let timeoutRef = null;
    return (...args) => {
        clearTimeout(timeoutRef);
        timeoutRef = setTimeout(() => func.apply(null, args), wait);
    };
};

/**
 * Higher-order function to create throttled function
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export const createThrottledFunction = (func, limit) => {
    let inThrottle = false;
    return (...args) => {
        if (!inThrottle) {
            func.apply(null, args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    };
};

/**
 * Pure function to create event handler that prevents default
 * @param {Function} handler - Original handler function
 * @returns {Function} Handler that prevents default before executing
 */
export const createPreventDefaultHandler = (handler) => {
    return (event) => {
        preventDefault(event);
        return handler(event);
    };
};

// ============================================================================
// BROWSER UTILITIES
// ============================================================================

/**
 * Pure function to open URL in new window
 * @param {string} url - URL to open
 * @param {string} target - Target window (_blank, _self, etc.)
 * @param {string} features - Window features
 * @returns {boolean} True if operation successful
 */
export const openUrlInNewWindow = (url, target = '_blank', features = 'noopener,noreferrer') => {
    if (url) {
        window.open(url, target, features);
        return true;
    }
    return false;
};

/**
 * Pure function to get current URL
 * @returns {string} Current page URL
 */
export const getCurrentUrl = () => window.location.href;

/**
 * Pure function to get URL parameters
 * @param {string} param - Parameter name
 * @returns {string|null} Parameter value or null if not found
 */
export const getUrlParameter = (param) => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
};

// ============================================================================
// INITIALIZATION UTILITIES
// ============================================================================

/**
 * Higher-order function to create DOM ready initializer
 * @param {Function} initFunction - Function to run when DOM is ready
 * @returns {Function} Initializer function
 */
export const createInitializer = (initFunction) => {
    return () => {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', initFunction);
        } else {
            initFunction();
        }
    };
};

/**
 * Higher-order function to create conditional initializer
 * @param {Function} condition - Function that returns boolean
 * @param {Function} initFunction - Function to run if condition is true
 * @param {number} maxAttempts - Maximum attempts (default: 50)
 * @param {number} interval - Interval between attempts in ms (default: 100)
 * @returns {Function} Conditional initializer
 */
export const createConditionalInitializer = (condition, initFunction, maxAttempts = 50, interval = 100) => {
    return () => {
        let attempts = 0;

        const checkAndInit = () => {
            attempts++;

            if (condition()) {
                initFunction();
            } else if (attempts < maxAttempts) {
                setTimeout(checkAndInit, interval);
            } else {
                console.warn('Conditional initializer: Maximum attempts reached');
            }
        };

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', checkAndInit);
        } else {
            checkAndInit();
        }
    };
};

// ============================================================================
// INTERSECTION OBSERVER UTILITIES
// ============================================================================

/**
 * Higher-order function to create intersection observer
 * @param {Function} callback - Callback function for intersection changes
 * @param {Object} options - Intersection observer options
 * @returns {IntersectionObserver} Configured intersection observer
 */
export const createIntersectionObserver = (callback, options = {}) => {
    const defaultOptions = {
        root: null,
        threshold: 0.1,
        rootMargin: '0px',
        ...options
    };

    return new IntersectionObserver(callback, defaultOptions);
};

/**
 * Pure function to observe element with intersection observer
 * @param {IntersectionObserver} observer - Intersection observer instance
 * @param {Element} element - Element to observe
 * @returns {boolean} True if operation successful
 */
export const observeElement = (observer, element) => {
    if (elementExists(element) && observer) {
        observer.observe(element);
        return true;
    }
    return false;
};

// ============================================================================
// STYLE UTILITIES
// ============================================================================

/**
 * Pure function to set CSS transform on element
 * @param {Element} element - DOM element
 * @param {string} transform - CSS transform value
 * @returns {boolean} True if operation successful
 */
export const setTransform = (element, transform) => {
    if (elementExists(element)) {
        element.style.transform = transform;
        return true;
    }
    return false;
};

/**
 * Pure function to set multiple CSS properties
 * @param {Element} element - DOM element
 * @param {Object} properties - Object with CSS properties
 * @returns {boolean} True if operation successful
 */
export const setStyles = (element, properties) => {
    if (!elementExists(element)) return false;

    Object.entries(properties).forEach(([property, value]) => {
        element.style[property] = value;
    });

    return true;
};

// ============================================================================
// VALIDATION UTILITIES
// ============================================================================

/**
 * Pure function to validate email format
 * @param {string} email - Email string to validate
 * @returns {boolean} True if email format is valid
 */
export const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

/**
 * Pure function to validate URL format
 * @param {string} url - URL string to validate
 * @returns {boolean} True if URL format is valid
 */
export const isValidUrl = (url) => {
    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
};

/**
 * Pure function to check if URL is external
 * @param {string} url - URL to check
 * @param {string[]} allowedDomains - Array of allowed domains
 * @returns {boolean} True if URL is external to allowed domains
 */
export const isExternalUrl = (url, allowedDomains = [window.location.hostname]) => {
    if (!url || url.startsWith('#') || url.startsWith('javascript:') || url.startsWith('mailto:')) {
        return false;
    }

    try {
        const urlObj = new URL(url);
        const domain = urlObj.hostname.toLowerCase();
        return !allowedDomains.some(allowedDomain =>
            domain === allowedDomain || domain.endsWith('.' + allowedDomain)
        );
    } catch {
        return false;
    }
};

// ============================================================================
// ARRAY & OBJECT UTILITIES
// ============================================================================

/**
 * Pure function to safely get nested property
 * @param {Object} obj - Object to traverse
 * @param {string} path - Dot notation path (e.g., 'user.profile.name')
 * @param {*} defaultValue - Default value if path doesn't exist
 * @returns {*} Value at path or default value
 */
export const getNestedProperty = (obj, path, defaultValue = undefined) => {
    return path.split('.').reduce((current, key) => {
        return (current && current[key] !== undefined) ? current[key] : defaultValue;
    }, obj);
};

/**
 * Pure function to check if array is empty
 * @param {Array} arr - Array to check
 * @returns {boolean} True if array is empty or not an array
 */
export const isEmptyArray = (arr) => !Array.isArray(arr) || arr.length === 0;

/**
 * Pure function to safely get array element
 * @param {Array} arr - Array to access
 * @param {number} index - Index to access
 * @param {*} defaultValue - Default value if index is invalid
 * @returns {*} Array element or default value
 */
export const safeArrayAccess = (arr, index, defaultValue = undefined) => {
    return (Array.isArray(arr) && index >= 0 && index < arr.length) ? arr[index] : defaultValue;
};
